import React, { useState, useRef, useEffect, useContext } from 'react';
import { Dropdown } from 'react-bootstrap';
import {
  MdSearch,
  MdNotifications,
  MdSettings,
  MdPerson,
  MdSwapHoriz,
  MdClose,
  MdMenu
} from 'react-icons/md';
import { Link, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import axios from 'axios';
import '../../../assets/css/components/customer/customerTopbar/CustomerTopbar.css';
import MyContext from '../../../context/MyContext';
import { getEcho } from '../../general/echo/Echo';

const getTimeAgo = (dateString) => {
  const seconds = Math.floor((new Date() - new Date(dateString)) / 1000);
  if (seconds < 60) return 'Just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours > 1 ? 's' : ''} ago`;
  const days = Math.floor(hours / 24);
  return `${days} day${days > 1 ? 's' : ''} ago`;
};

// Notification "unread" ki definition robust ban chuki hai — status ki value
// database mein 'unread', 'Unread', ya kabhi kabhi garbage (0, null) bhi ho
// sakti hai. Is liye "read" ke elawa sab kuch unread mana jata hai.
const isUnread = (status) => String(status).toLowerCase() !== 'read';

const CustomerTopbar = ({ sidebarOpen, setSidebarOpen }) => {

  const navigate = useNavigate();
  const {
    baseUrl,
    authToken,
    setAuthToken,
    setIsLoggedIn,
    setLoggedInSeller,
    loggedInCustomer,
  } = useContext(MyContext);

  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const notificationRef = useRef(null);

  const fetchNotifications = () => {
    axios.get(`${baseUrl}/customer/notifications/viewAllNotifications`, {
      headers: { Authorization: `Bearer ${authToken}` }
    })
      .then((response) => {
        if (response.data.success) {
          setNotifications(response.data.notifications);
        }
      })
      .catch((error) => {
        console.log(error);
      });
  };

  useEffect(() => {
    if (!authToken) return;
    fetchNotifications();
  }, [authToken]);

  // ── real-time — nayi notification aate hi list ke top pe daal do,
  // badge count apne aap update ho jayega kyunke unreadNotifications
  // usi notifications state se calculate hota hai ──
  useEffect(() => {
    if (!authToken || !loggedInCustomer?.id) return;

    const echo = getEcho(baseUrl, authToken);
    if (!echo) return;

    const channelName = `customer.${loggedInCustomer.id}`;
    const channel = echo.private(channelName);

    channel.listen('.new-notification', (e) => {
      const incoming = e.notification;
      setNotifications((prev) => {
        if (prev.some((n) => n.id === incoming.id)) return prev;
        return [incoming, ...prev];
      });
    });

    return () => {
      echo.leave(channelName);
    };
  }, [authToken, loggedInCustomer?.id]);

  useEffect(() => {
    const clickOutside = (e) => {
      if (notificationRef.current && !notificationRef.current.contains(e.target)) {
        setShowNotifications(false);
      }
    };
    document.addEventListener('mousedown', clickOutside);
    return () => document.removeEventListener('mousedown', clickOutside);
  }, []);

  // 🔁 Switch to Seller
  const switchToSeller = () => {
    Swal.fire({
      title: 'Switch to Seller Account?',
      text: 'You will be redirected to seller dashboard',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Yes',
      confirmButtonColor: '#097d43',
    }).then((result) => {
      if (!result.isConfirmed) return;

      axios.post(`${baseUrl}/customer/auth/switchToSeller`, {}, {
        headers: { Authorization: `Bearer ${authToken}` }
      })
        .then((response) => {
          if (response.data.success) {
            localStorage.setItem('authToken', response.data.token);
            localStorage.setItem('loggedInSeller', JSON.stringify(response.data.seller));
            setAuthToken(response.data.token);
            setLoggedInSeller(response.data.seller);
            setIsLoggedIn(true);
            Swal.fire({
              title: 'Switched!',
              text: 'Successfully switched to seller account',
              icon: 'success',
              confirmButtonColor: '#097d43',
              timer: 1500
            }).then(() => navigate('/seller/dashboard'));
          } else {
            Swal.fire({ icon: 'error', title: 'Error', text: 'Could not switch account.' });
          }
        })
        .catch((error) => {
          console.log(error);
          Swal.fire({ icon: 'error', title: 'Error', text: 'Could not switch account.' });
        });
    });
  };

  // ── notification pe click: mark as read + notifications page
  // par navigate karo us particular notification ke sath, taake
  // wahan uski details modal khul jaye ──
  const openNotification = (n) => {
    setShowNotifications(false);

    if (isUnread(n.status)) {
      const formData = new FormData();
      formData.append('id', n.id);

      axios
        .post(`${baseUrl}/customer/notifications/changeNotificationStatus`, formData, {
          headers: { Authorization: `Bearer ${authToken}` },
        })
        .then(() => {
          setNotifications((prev) =>
            prev.map((item) =>
              item.id === n.id ? { ...item, status: 'read' } : item,
            ),
          );
        })
        .catch((error) => console.log(error));
    }

    navigate('/customer/notifications', { state: { openNotification: n } });
  };

  const unreadNotifications = notifications.filter(n => isUnread(n.status)).length;

  return (
    <div className="sel-topbar">

      {/* Toggle */}
      <button className="sel-topbar__toggle" onClick={() => setSidebarOpen(!sidebarOpen)}>
        <MdMenu />
      </button>

      {/* Right Actions */}
      <div className="sel-topbar__actions">

        {/* Notifications */}
        <div className="sel-topbar__dropdown-wrap" ref={notificationRef}>
          <button
            className="sel-topbar__icon-btn"
            onClick={() => setShowNotifications(!showNotifications)}
          >
            <MdNotifications />
            {unreadNotifications > 0 && (
              <span className="sel-topbar__badge">{unreadNotifications}</span>
            )}
          </button>

          {showNotifications && (
            <div className="sel-topbar__dropdown">
              <div className="sel-topbar__dropdown-header">
                <h6>Notifications</h6>
                <button onClick={() => setShowNotifications(false)}>
                  <MdClose />
                </button>
              </div>

              <div className="sel-topbar__dropdown-body">
                {notifications.filter((n) => isUnread(n.status)).map((n) => (
                  <div
                    key={n.id}
                    className="sel-topbar__dropdown-item sel-topbar__dropdown-item--unread"
                    onClick={() => openNotification(n)}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="sel-topbar__notif-content">
                      <h6>{n.title}</h6>
                      <p>{n.message}</p>
                      <span>{getTimeAgo(n.created_at)}</span>
                    </div>
                  </div>
                ))}
                {notifications.filter((n) => isUnread(n.status)).length === 0 && (
                  <div className="sel-topbar__dropdown-empty">No new notifications</div>
                )}
              </div>

              <div className="sel-topbar__dropdown-footer">
                <Link to="/customer/notifications">View All</Link>
              </div>
            </div>
          )}
        </div>

        {/* Settings */}
        <button
          className="sel-topbar__icon-btn"
          onClick={() => navigate('/customer/profile')}
        >
          <MdSettings />
        </button>

        {/* Profile */}
        <Dropdown>
          <Dropdown.Toggle as="div" className="sel-topbar__profile">
            <div className="sel-topbar__profile-info">
              <h6>{loggedInCustomer?.name}</h6>
              <p>{loggedInCustomer?.email}</p>
            </div>
          </Dropdown.Toggle>

          <Dropdown.Menu align="end">

            <Dropdown.Item onClick={() => navigate('/customer/profile')}>
              <MdPerson /> My Profile
            </Dropdown.Item>

            <Dropdown.Item onClick={() => navigate('/customer/profile')}>
              <MdSettings /> Settings
            </Dropdown.Item>

            <Dropdown.Divider />

            <Dropdown.Item onClick={() => switchToSeller()}>
              <MdSwapHoriz /> Switch to Seller
            </Dropdown.Item>

          </Dropdown.Menu>
        </Dropdown>

      </div>
    </div>
  );
};

export default CustomerTopbar;
